// Centralized helpers for surfacing Supabase / Postgres errors to the
// user. Replaces the swallowed console.error calls and the
// inconsistent Alert / inline-string patterns spread across pages.

import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Maps a Postgres / Supabase / generic error to a friendly user-facing
 * message. Recognizes common SQLSTATE codes and our own application
 * P0001 RAISEs (used by sync RPCs etc.) and falls back to the raw
 * `message` for everything else.
 */
export function toUserMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (!err) return fallback;
  // Supabase / PostgrestError shape
  const e = err as Partial<PostgrestError> & { message?: string; code?: string };
  if (e?.code && e?.message) {
    switch (e.code) {
      case '23505': return 'That value already exists. Pick a different one.';                // unique_violation
      case '23503': return 'Linked record is missing or has been removed.';                    // foreign_key_violation
      case '23502': return 'A required field is missing.';                                     // not_null_violation
      case '23514': return 'The value isn\'t allowed by the rules on this field.';             // check_violation
      case '42501': return 'You don\'t have permission for that action.';                      // insufficient_privilege
      case '42P01': return 'A required table is missing — the database may need a migration.'; // undefined_table
      case 'PGRST116': return 'No matching record found.';
      case 'PGRST301': return 'Authentication required. Try signing back in.';
      case 'P0001':
        // Application-raised exceptions from our RPCs (with usable messages).
        return e.message;
    }
    return e.message;
  }
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === 'string') return err;
  return fallback;
}
